import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import './style.css';
import { ReactComponent as Doctor } from '../../../../../assets/img/pharmacy/DOCTOR.svg';
import { ReactComponent as MedicalCommission } from '../../../../../assets/img/pharmacy/medicalCommission.svg';
import { ReactComponent as Recipe } from '../../../../../assets/img/pharmacy/recipe.svg';
import { ReactComponent as Stamp } from '../../../../../assets/img/pharmacy/stamp.svg';
import { ReactComponent as Stamp1 } from '../../../../../assets/img/pharmacy/Stamp1.svg';
import { ReactComponent as Stamp2 } from '../../../../../assets/img/pharmacy/Stamp2.svg';
import { ReactComponent as Stamp4 } from '../../../../../assets/img/pharmacy/Stamp4.svg';
import { ReactComponent as Special1 } from '../../../../../assets/img/pharmacy/Special1.svg';
import { ReactComponent as Special2 } from '../../../../../assets/img/pharmacy/Special2.svg';
import { ReactComponent as Special5 } from '../../../../../assets/img/pharmacy/Special5.svg';
import { ReactComponent as Recipe1 } from '../../../../../assets/img/pharmacy/Recipe1.svg';
import { ReactComponent as Doctor1 } from '../../../../../assets/img/pharmacy/Doctor1.svg';

interface Form14804BackProps {
  onInputChange?: (id: number, value: string) => void;
  initialValues: InitialValuesType;
  onCheckboxStateChange?: (checkboxStates: {
    inputs: { [key: number]: boolean };
    svgs: { [key: number]: boolean };
  }) => void;
  isShow?: boolean;
  isReadOnly?: boolean;
  isShowDeleteButton?: boolean;
  formDroppedItems?: SvgType[];
  handleDragStart?: (index: number, type: string) => void;
  handleDragOver?: () => void;
  handleDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onSvgDelete?: (index: number, id: number) => void;
  isConstructor?: boolean;
  isChatPage?: boolean;
}

interface InitialValuesType {
  inputs?: {
    [key: number]: {
      value: string | undefined;
      right?: boolean;
      [key: string]: any;
    };
  };
  svgs?: Array<SvgType>;
}

interface SvgType {
  id: number;
  type: string;
  x: number;
  y: number;
  isWrongAnswer: boolean;
}

const Form14804Back = forwardRef(({
  initialValues,
  onInputChange,
  formDroppedItems = [],
  handleDragStart,
  handleDragOver,
  handleDrop,
  onSvgDelete,
  isShow,
  isReadOnly,
  onCheckboxStateChange,
  isConstructor,
  isChatPage
}: Form14804BackProps, ref) => {
  const [inputCheckboxStates, setInputCheckboxStates] = useState<{ [key: number]: boolean }>(() => {
    if (initialValues?.inputs) {
      const initialCheckboxStates = Object.fromEntries(
        Object.entries(initialValues.inputs).map(([id, input]) => [
          Number(id),
          input.right || false,
        ])
      );
      return initialCheckboxStates;
    }
    return {};
  });

  const [svgCheckboxStates, setSvgCheckboxStates] = useState<{ [key: number]: boolean }>(() => {
    if (initialValues?.svgs) {
      const initialSvgCheckboxStates = Object.fromEntries(
        initialValues.svgs.map((svg) => [svg.id, svg.isWrongAnswer || false])
      );
      return initialSvgCheckboxStates;
    }
    return {};
  });

  const [values, setValues] = useState(() => ({
    inputs: initialValues?.inputs || {},
  }));

  useEffect(() => {
    setValues((prevValues) => ({
      inputs: {
        ...prevValues.inputs,
        ...initialValues.inputs,
      },
    }));
  }, [initialValues.inputs]);

  const resetAllFields = () => {
    setValues({ inputs: {} });
    setInputCheckboxStates({});
    setSvgCheckboxStates({});
  };

  useImperativeHandle(ref, () => ({
    resetAllFields,
  }));

  const handleInputChangeLocal = (id: number, value: string) => {
    setValues((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [id]: {
          ...prev.inputs[id],
          value,
        },
      },
    }));
    onInputChange?.(id, value);
  };

  const handleInputCheckboxChange = (id: number) => {
    setInputCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      setValues((prevValues) => ({
        inputs: {
          ...prevValues.inputs,
          [id]: {
            ...prevValues.inputs[id],
            right: newState[id],
          },
        },
      }));

      onCheckboxStateChange?.({ inputs: newState, svgs: svgCheckboxStates });
      return newState;
    });
  };

  const handleSvgCheckboxChange = (id: number) => {
    setSvgCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      onCheckboxStateChange?.({ inputs: inputCheckboxStates, svgs: newState });
      return newState;
    });
  };

  const getSvgStyle = (type: string, x: number, y: number) => {
    const baseStyle = {
      position: 'absolute' as const,
      cursor: 'move'
    };

    switch (type) {
      case 'Doctor':
        return {
          ...baseStyle,
          left: `${x - 150}px`,
          top: `${y - 40}px`,
          transform: 'scale(1)',
        };
      case 'MedicalCommission':
        return {
          ...baseStyle,
          left: `${x - 190}px`,
          top: `${y + 20}px`,
          transform: 'scale(1.1)',
        };
      case 'Recipe':
        return {
          ...baseStyle,
          left: `${x -120}px`,
          top: `${y + 100}px`,
          transform: 'scale(1)',
        };
      case 'Stamp':
        return {
          ...baseStyle,
          left: `${x - 90}px`,
          top: `${y - 0}px`,
          transform: 'scale(1.2)',
        };
      case 'Stamp1':
        return {
          ...baseStyle,
          left: `${x - 190}px`,
          top: `${y -30}px`,
          transform: 'scale(1)',
        };
      case 'Stamp2':
        return {
          ...baseStyle,
          left: `${x - 200}px`,
          top: `${y - 0}px`,
          transform: 'scale(1.3)',
        };
      case 'Stamp4':
        return {
          ...baseStyle,
          left: `${x - 295}px`,
          top: `${y + 45}px`,
          transform: 'scale(0.5)',
        };
      case 'Special1':
        return {
          ...baseStyle,
          left: `${x - 120}px`,
          top: `${y + 110}px`,
          transform: 'scale(0.6)',
        };
      case 'Special2':
        return {
          ...baseStyle,
          left: `${x -85}px`,
          top: `${y -40}px`,
          transform: 'scale(0.75)',
        };
      case 'Special5':
        return {
          ...baseStyle,
          left: `${x + 5}px`,
          top: `${y -40}px`,
          transform: 'scale(0.5)',
        };
      case 'Recipe1':
        return {
          ...baseStyle,
          left: `${x + 95}px`,
          top: `${y + 60}px`,
          transform: 'scale(1.3)',
        };
      case 'Doctor1':
        return {
          ...baseStyle,
          left: `${x + 120}px`,
          top: `${y - 70}px`,
          transform: 'scale(1.1)',
        };
      default:
        return {
          ...baseStyle,
          left: `${x}px`,
          top: `${y}px`,
          transform: 'scale(1)',
        };
    }
  };

  return (
    <div
      style={{
        width: '1200px',
        margin: '0 auto',
        position: 'relative',
        border: '1px solid rgba(34, 34, 34, 0.31)',
        padding: 20,
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div style={{ marginTop: 10 }}>
        <span>Оборотная сторона</span>
      </div>

      {/* SVG Elements */}
      {[...initialValues.svgs || [], ...formDroppedItems].map((svg, index) => (
        <div
          key={`svg-${svg.id}-${index}`}
          style={getSvgStyle(svg.type, svg.x, svg.y)}
          draggable={isConstructor}
          onDragStart={() => handleDragStart?.(index, 'response')}
        >
          {svg.type === 'Doctor' && <Doctor />}
          {svg.type === 'MedicalCommission' && <MedicalCommission />}
          {svg.type === 'Recipe' && <Recipe />}
          {svg.type === 'Stamp' && <Stamp />}
          {svg.type === 'Stamp1' && <Stamp1 />}
          {svg.type === 'Stamp2' && <Stamp2 />}
          {svg.type === 'Stamp4' && <Stamp4 />}
          {svg.type === 'Special1' && <Special1 />}
          {svg.type === 'Special2' && <Special2 />}
          {svg.type === 'Special5' && <Special5 />}
          {svg.type === 'Recipe1' && <Recipe1 />}
          {svg.type === 'Doctor1' && <Doctor1 />}
          {isShow && (
            <input
              type="checkbox"
              checked={svgCheckboxStates[svg.id] || false}
              style={{ position: 'absolute', top: -10, left: -10 }}
              onChange={() => handleSvgCheckboxChange(svg.id)}
            />
          )}
          {isConstructor && (
            <button
              style={{
                position: 'absolute',
                background: '#ff665e',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                width: 20,
                height: 20,
                top: -10,
                right: -10,
                cursor: 'pointer',
              }}
              onClick={() => onSvgDelete?.(index, svg.id)}
            >
              ×
            </button>
          )}
        </div>
      ))}

      {/* Inputs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 40 }}>
        {[1, 2, 3].map((id) => (
          <div
            key={`input-${id}`}
            style={{ display: 'flex', flexDirection: 'column', border: '1px solid black', width: 300 }}
          >
            <div style={{ borderBottom: '1px solid black', padding: 15, textAlign: 'center' }}>
              <span>{id === 1 ? 'Приготовил' : id === 2 ? 'Проверил' : 'Отпустил'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <input
                type="text"
                value={values.inputs?.[id]?.value || ''}
                onChange={(e) => handleInputChangeLocal(id, e.target.value)}
                disabled={isReadOnly}
                style={{ width: '98%', border: 'none', height: 25, fontSize: '14pt' }}
              />
              {isShow && (
                <input
                  type="checkbox"
                  checked={inputCheckboxStates[id] || false}
                  onChange={() => handleInputCheckboxChange(id)}
                  style={{ marginLeft: 10 }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default Form14804Back;
